<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';
import Buttons from '@/components/ui/Buttons.vue';
import RedSpot from '@/components/RedSpot.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  redSpot,
  blocks,
  buttons,
  hasBlocks,
  hasTitle,
  hasButtons,
  hasRedSpot,
  hasAnyContent,
  template,
} = useBlockSafe(props.data);
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor && template !== 'article' ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <RedSpot
      v-if="hasRedSpot"
      :position-vertical="redSpot.vertical"
      :position-horizontal="redSpot.horizontal"
    />
    <div class="container">
      <div class="loyalty-terms">
        <h2
          v-if="hasTitle"
          class="mb-0 loyalty-terms__title h2"
          :class="[backgroundColor === 'black' ? 'white' : 'black']"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="title"
        ></h2>
        <div v-if="hasBlocks || hasButtons" class="loyalty-terms__bottom">
          <div v-if="hasBlocks" class="loyalty-terms__blocks">
            <template v-for="(block, blockIndex) in blocks" :key="blockIndex">
              <div
                v-if="block.title || (block.items && block.items.length > 0)"
                :class="[
                  `loyalty-terms__block loyalty-terms__block--${backgroundColor}`,
                ]"
                data-aos="fade-up"
                :data-aos-delay="blockIndex >= 1 ? 100 + 100 * blockIndex : 100"
              >
                <p
                  v-if="block.title"
                  class="mb-0 loyalty-terms__block-title h5"
                  v-html="block.title"
                ></p>
                <div
                  v-if="block.items && block.items.length > 0"
                  :class="[
                    `loyalty-terms__block-items loyalty-terms__block-items--${block.columns}`,
                  ]"
                >
                  <template
                    v-for="(item, itemIndex) in block.items"
                    :key="itemIndex"
                  >
                    <div
                      v-if="
                        item.title ||
                        item.subtitle ||
                        (item.badges && item.badges.length > 0)
                      "
                      class="loyalty-terms__block-item"
                    >
                      <div
                        v-if="item.title || item.subtitle"
                        class="loyalty-terms__block-item-top"
                      >
                        <p
                          v-if="item.title"
                          class="mb-0 loyalty-terms__block-item-title h6-mobile"
                          v-html="item.title"
                        ></p>
                        <span
                          v-if="item.subtitle"
                          class="loyalty-terms__block-item-subtitle body-3-regular"
                          v-html="item.subtitle"
                        ></span>
                      </div>
                      <div
                        v-if="item.badges && item.badges.length > 0"
                        :class="[
                          'loyalty-terms__block-item-badges',
                          item.badges && item.badges.length === 1
                            ? 'loyalty-terms__block-item-badges--1'
                            : '',
                        ]"
                      >
                        <template
                          v-for="(badge, badgeIndex) in item.badges"
                          :key="badgeIndex"
                        >
                          <div
                            v-if="badge.value || badge.label"
                            class="loyalty-terms__block-item-badge"
                          >
                            <span
                              v-if="badge.value"
                              class="loyalty-terms__block-item-badge-value body-1-bold"
                              v-html="badge.value"
                            ></span>
                            <span
                              v-if="badge.label"
                              class="loyalty-terms__block-item-badge-label body-3-regular"
                              v-html="badge.label"
                            ></span>
                          </div>
                        </template>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </div>
          <Buttons
            v-if="hasButtons"
            class="loyalty-terms__buttons"
            :buttons="buttons"
            :background-color="backgroundColor"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            :buttons-prefix-class="'js-button-loyalty-terms'"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.loyalty-terms {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__title {
    margin: 0 auto;
    text-align: center;
    @include media-breakpoint-up(md) {
      width: 66%;
    }
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 56px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 56);
    }
  }
  &__blocks {
    display: flex;
    flex-direction: column;
    gap: 24px;
    @include media-breakpoint-up(sm) {
      gap: 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
  }
  &__block {
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 56px;
    text-align: center;
    @include media-breakpoint-up(sm) {
      gap: 80px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(gap, 80);
    }
    &--white {
      background-color: $gray;
      #{$parent} {
        &__block-item {
          border-color: $white;
        }
      }
    }
    &--black {
      background-color: rgba($surface-container-opacity, 0.5);
      color: $white;
    }
    &--gray {
      background-color: $white;
      #{$parent} {
        &__block-item {
          border-color: $divider;
        }
      }
    }
    &-title {
      padding: 56px 16px 0;
      @include media-breakpoint-up(sm) {
        padding-top: 80px;
        max-width: 464px;
        margin: 0 auto;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(max-width, 464);
        @include responsive-spacing(padding-top, 80);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-right, 16);
      }
    }
    &-items {
      display: grid;
      grid-template-columns: 1fr;
      &--2 {
        @include media-breakpoint-up(sm) {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      &--3 {
        @include media-breakpoint-up(sm) {
          grid-template-columns: repeat(3, 1fr);
        }
      }
    }
    &-item {
      border-top: 1px solid $on-surface-variant-2;
      display: flex;
      flex-direction: column;
      gap: 24px;
      padding: 32px;
      @include media-breakpoint-up(sm) {
        border-left: 1px solid $on-surface-variant-2;
        gap: 40px;
        padding: 48px 32px;
        &:first-child {
          border-left: unset;
        }
      }
      @include media-breakpoint-up(md) {
        padding: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 40);
        @include responsive-spacing(padding, 48);
      }
      &-top {
        display: flex;
        flex-direction: column;
        gap: 16px;
        @include media-breakpoint-up(md) {
          margin: 0 auto;
          max-width: 320px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 16);
          @include responsive-spacing(max-width, 320);
        }
      }
      &-subtitle {
        color: $on-surface-variant;
      }
      &-badges {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;
        &--1 {
          grid-template-columns: 1fr;
        }
        @include media-breakpoint-up(sm) {
          grid-template-columns: 1fr;
          gap: 24px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 24);
        }
      }
      &-badge {
        align-items: center;
        display: flex;
        flex-direction: column;
        gap: 8px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 8);
        }
        &-value {
          align-items: center;
          background-color: $primary;
          border-radius: 50%;
          color: $white;
          display: inline-flex;
          justify-content: center;
          height: 64px;
          width: 64px;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(height, 64);
            @include responsive-spacing(width, 64);
          }
        }
      }
    }
  }
  &__buttons {
    justify-content: center;
  }
}
</style>
